import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React from "react";

import { patientPresentingConcernsApi } from "../../../apis";
import { PresentingConcernsResponseDto } from "../../../libs/cellera-sdk";
import { useCelleraTheme } from "../../../theme/useCelleraTheme";
import IntakeField from "./IntakeField";
import IntakeHeader from "./IntakeHeader";

interface PresentingConcernsProps {
  patientId: string;
}

const PresentingConcerns: React.FC<PresentingConcernsProps> = ({
  patientId,
}) => {
  const { colors } = useCelleraTheme();

  const { data, isError, error, isLoading } = useQuery({
    queryKey: ["presentingConcerns", patientId],
    queryFn: async () => {
      if (!patientId) return null;
      return patientPresentingConcernsApi.getPresentingConcernsByPatientId({
        patientId,
      });
    },
    enabled: !!patientId,
  });

  React.useEffect(() => {
    if (isError && error) {
      console.error("PresentingConcerns query error:", error);
    }
  }, [isError, error]);

  const displayData: PresentingConcernsResponseDto = data ?? {
    patientId,
    presentingConcerns: [],
  };

  if (isLoading) {
    return (
      <>
        <IntakeHeader title="Presenting Concerns" />
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography color={colors.text.secondary}>
            Loading presenting concerns...
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <IntakeHeader title="Presenting Concerns" />

      <Box>
        {displayData.presentingConcerns.length === 0 ? (
          <Box
            sx={{
              backgroundColor: colors.background.paper,
              borderRadius: "8px",
              border: `1px solid ${colors.border.light}`,
            }}
          >
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography
                variant="h6"
                color={colors.text.secondary}
                gutterBottom
              >
                No Presenting Concerns Found
              </Typography>
              <Typography variant="body2" color={colors.text.secondary}>
                This patient has not filled out their presenting concerns yet.
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {displayData.presentingConcerns.map((concern, index) => (
              <>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: colors.text.primary,
                    fontWeight: 600,
                  }}
                >
                  Health Concern #{index + 1}
                </Typography>

                {/* Primary Health Concerns */}
                <IntakeField
                  label={`Health Concern #${index + 1}`}
                  value={concern.primaryHealthConcerns || ""}
                  multiline={true}
                />

                {/* When First Noticed */}
                {
                  <IntakeField
                    label="When First Noticed? (Optional)"
                    value={concern.whenFirstNoticed}
                    multiline={true}
                  />
                }

                {/* Treatments Tried */}
                {
                  <IntakeField
                    label="Treatments Tried (Optional)"
                    value={concern.treatmentsTried}
                    multiline={true}
                  />
                }
              </>
            ))}
          </Box>
        )}
      </Box>
    </>
  );
};

export default PresentingConcerns;
